import { Alert, AlertAction, AlertDescription } from '@monksflow/monks-ui';
import { CircleAlertIcon, XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { ChatAlertProps } from './ChatAlert.types';

/**
 * Inline, informational alert for the chat composer — a thin wrapper over the
 * monks-ui `Alert` primitive styled to the Sessions "Alert" Figma spec
 * (surface background, hairline border, rounded-2xl, subtle foreground text).
 *
 * Used to surface soft, non-blocking notices such as an automatic model switch
 * ("AI model switched to Gemini 2.5 Pro. Video attachments are only supported
 * by Google models."). Optionally dismissible.
 */
export const ChatAlert = ({ message, onDismiss }: ChatAlertProps) => {
  const { t } = useTranslation('chats');

  return (
    <Alert
      data-testid="chat-alert"
      role="status"
      variant="default"
      icon={<CircleAlertIcon className="size-4 shrink-0 text-foreground-subtle" />}
      className="items-start gap-3 rounded-2xl border-[0.5px] border-border-light bg-surface p-4 text-foreground-subtle"
    >
      <AlertDescription className="text-sm leading-5 text-foreground-subtle">
        {message}
      </AlertDescription>
      {onDismiss && (
        <AlertAction>
          <button
            type="button"
            onClick={onDismiss}
            aria-label={t('chats.chatAlert.dismiss')}
            className="shrink-0 text-foreground-subtle transition-colors hover:text-foreground"
          >
            <XIcon className="size-4" />
          </button>
        </AlertAction>
      )}
    </Alert>
  );
};
